import { FileValidationError } from './types';

export const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500 MB
export const MAX_FILES_PER_BATCH = 100;

export const ACCEPTED_CONTENT_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  dotx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ppt: 'application/vnd.ms-powerpoint',
  ppsx: 'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  odt: 'application/vnd.oasis.opendocument.text',
  odp: 'application/vnd.oasis.opendocument.presentation',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  rtf: 'application/rtf',
  epub: 'application/epub+zip',
  eml: 'message/rfc822',
  msg: 'application/vnd.ms-outlook',
  txt: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',
  html: 'text/html',
  htm: 'text/html',
  // Images
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif'
};

export const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);

export const ACCEPTED_EXTENSIONS = new Set(Object.keys(ACCEPTED_CONTENT_TYPES));
export const ACCEPTED_MIME_SET = new Set(Object.values(ACCEPTED_CONTENT_TYPES));

export function getExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

export function inferContentType(file: File): string {
  const ext = getExtension(file.name);
  return ACCEPTED_CONTENT_TYPES[ext] ?? file.type ?? 'application/octet-stream';
}

export function validationErrorMessage(error: FileValidationError): string {
  const labels: Record<FileValidationError, string> = {
    too_large: 'File too large',
    unsupported_type: 'Unsupported file type',
    unsafe_name: 'Invalid filename',
    duplicate: 'Duplicate file'
  };
  return labels[error];
}

export function validateFile(file: File, existingSha256s: Set<string>): FileValidationError | undefined {
  const ext = getExtension(file.name);
  if (!ACCEPTED_EXTENSIONS.has(ext) && ext !== 'zip') {
    return 'unsupported_type';
  }
  const contentType = inferContentType(file);
  if (!ACCEPTED_MIME_SET.has(contentType)) {
    return 'unsupported_type';
  }
  if (file.size > MAX_FILE_SIZE) {
    return 'too_large';
  }
  if (/[/\\<>:"'|?*\x00-\x1f]/.test(file.name) || file.name.includes('..')) {
    return 'unsafe_name';
  }
  return undefined;
}

/**
 * Convert an API error detail into text suitable for display.
 *
 * FastAPI validation failures use an array of objects rather than a string,
 * while other endpoints may return a single `{ message }` object.
 */
export function formatUploadErrorDetail(detail: unknown, fallback = 'Upload failed'): string {
  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'msg' in item && typeof item.msg === 'string') {
          return item.msg;
        }
        return undefined;
      })
      .filter((message): message is string => Boolean(message));
    if (messages.length > 0) return messages.join('; ');
  }

  if (detail && typeof detail === 'object' && 'message' in detail && typeof detail.message === 'string') {
    return detail.message;
  }

  return fallback;
}

/** Parse a 422 batch validation message to the file name it refers to, if any. */
export function parseBatchValidationFileName(detail: unknown): string | undefined {
  if (typeof detail !== 'string') return undefined;
  const match = detail.match(/File '([^']+)'/);
  return match?.[1];
}

/**
 * Shown when the browser cannot read a local file. Office apps on Windows
 * keep an exclusive lock while the document is open, so the picker succeeds
 * but `arrayBuffer()` / the upload stream fail.
 */
export const FILE_IN_USE_MESSAGE = 'Could not read this file. Close it in Word, Excel, or another app and try again.';

export function isUnreadableFileError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const name = 'name' in error ? String((error as { name?: unknown }).name) : '';
  const message = 'message' in error ? String((error as { message?: unknown }).message) : '';
  if (name === 'NotReadableError' || name === 'NotFoundError' || name === 'SecurityError') return true;
  return /NotReadableError|NotFoundError|ERR_UPLOAD_FILE_CHANGED|could not be read|not readable/i.test(message);
}

/**
 * Copy the file into memory so the Azure PUT does not stream from a path
 * that Word/Excel may still have locked.
 */
export async function snapshotLocalFile(file: File): Promise<File> {
  try {
    const buffer = await file.arrayBuffer();
    return new File([buffer], file.name, {
      type: file.type,
      lastModified: file.lastModified
    });
  } catch {
    throw new Error(FILE_IN_USE_MESSAGE);
  }
}

/** Compute SHA-256 hash of a File using the Web Crypto API. */
export async function computeSha256(file: Blob): Promise<string> {
  let buffer: ArrayBuffer;
  try {
    buffer = await file.arrayBuffer();
  } catch {
    throw new Error(FILE_IN_USE_MESSAGE);
  }
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending_upload: 'Queued',
    uploaded: 'Uploading…',
    security_scan_pending: 'Scanning…',
    security_scanning: 'Scanning…',
    clean: 'Processing…',
    infected: 'Blocked — infected',
    scan_failed: 'Scan failed',
    rejected: 'Rejected',
    ready: 'Ready',
    failed: 'Failed'
  };
  return labels[status] ?? status;
}

export function isTerminalStatus(status: string): boolean {
  return ['ready', 'infected', 'rejected', 'scan_failed', 'failed'].includes(status);
}

export function isFailed(status: string): boolean {
  return ['infected', 'rejected', 'scan_failed', 'failed'].includes(status);
}

export function isReadyForUse(status: string): boolean {
  return status === 'ready';
}

export function truncateMiddle(name: string, maxLength = 42): string {
  if (name.length <= maxLength) return name;
  const startChars = Math.ceil(maxLength * 0.6);
  const endChars = maxLength - startChars;
  return `${name.slice(0, startChars)}…${name.slice(-endChars)}`;
}
